import axios from 'axios'
import { myAvantEndpointMock } from '../../helpers/myAvantApiMock'
import { getEnvVar } from '../../helpers/environment'

const avantBasicUrl = getEnvVar('AVANT_BASIC_URL')

/**
 * @typedef {Object} RedeemOfferCodeRequest
 * @property {string} code
 * @property {string} ssn
 * @property {'installment'|'credit_card'} productType
 * @property {string} requestId
 */

/**
 * @typedef {Object} RedeemOfferCodeResponse
 * @property {boolean} success
 * @property {string} customer_application_id
 * @property {string} applicationURL
 * @property {string} error
 */

/**
 * API call to redeem a direct mail offer code endpoint
 * @param {RedeemOfferCodeRequest} request
 * @returns {Promise<RedeemOfferCodeResponse>}
 */
async function redeemOfferCode({ code, ssn, productType, requestId }) {
  const segmentId = window.analytics?.user?.()?.anonymousId() ?? null
  if (window.env.MOCK_ACTIVATE === 'true') {
    return myAvantEndpointMock(code, ssn, segmentId)
  }

  const client = axios.create({
    baseURL: avantBasicUrl,
    withCredentials: true
  })

  const path = productType === 'installment' ? 'personal_offer_code_async' : 'card_offer'
  const body = { code, ssn, product_type: productType }
  const response = await client.post(path, body)
  const { success, customer_application_id } = response.data

  const searchParams = new URLSearchParams(window.location.search)

  if (requestId) {
    searchParams.set('affiliate_request_primary_key', requestId)
  }
  if (success && segmentId !== null) {
    searchParams.set('segment_id', segmentId)
  }

  const applicationURL = new URL(`${avantBasicUrl}/apply/${customer_application_id}`)
  applicationURL.search = searchParams.toString()
  return { ...response.data, applicationURL: applicationURL.toString() }
}

/**
 * @typedef {Object} RedeemOfferCodeAdapterResponse
 * @property {boolean} success
 * @property {string} destination
 * @property {RedeemOfferCodeResponse} requestResponse
 * @property {number} status
 */

/**
 * @typedef {Object} RedeemOfferCodeAdapterFormData
 * @property {string} personal_offer_code
 * @property {string} last_4_of_ssn
 */

/**
 * Will perform the the direct mail redemption api call and add the response to the apiResponse object expected by the Form component
 * @param {RedeemOfferCodeAdapterFormData} formData
 * @param {'installment'|'credit_card'} productType
 * @returns {Promise<RedeemOfferCodeAdapterResponse>}
 */
async function redeemOfferCodeAdapter(formData, productType) {
  const { personal_offer_code, last_4_of_ssn } = formData

  const apiResponse = {
    response: null,
    destination: null,
    requestResponse: null,
    success: null,
    status: null
  }
  try {
    apiResponse.response = await redeemOfferCode({
      code: personal_offer_code,
      ssn: last_4_of_ssn,
      productType
    })
    apiResponse.destination = apiResponse.response.applicationURL
    apiResponse.requestResponse = 'Successful Offer'
    apiResponse.success = apiResponse.response.success
    apiResponse.status = 200
  } catch (err) {
    apiResponse.response = err.response
    apiResponse.destination = 'Unsuccessful destination'
    apiResponse.requestResponse = err.response.data.error
    apiResponse.status = 404
    apiResponse.success = false
  }
  apiResponse.modal = true
  return apiResponse
}

export { redeemOfferCode, redeemOfferCodeAdapter }
